/**
 * Folder Grid View - Icon grid display mode
 *
 * Selection is managed centrally via useSelectionStore.
 * This component only renders items and passes selection state down.
 *
 * @module features/workspace/components/FolderView/FolderGridView
 */

import { useRef, useEffect, useMemo } from 'react';
import type { WorkspaceItemType } from '@loader/shared-types';

import { WorkspaceItemCard } from '../WorkspaceItemCard';
import type { WorkspaceItem, ViewMode } from './types';
import { useSelectionStore } from '../../stores/useSelectionStore';
import { useSelectionPreviewSync } from '../../hooks/useSelectionPreviewSync';

interface FolderGridViewProps {
  items: WorkspaceItem[];
  viewMode: ViewMode;
  isTrash?: boolean;
  /** Whether user can edit (editor/owner) - viewers cannot modify items */
  canEdit?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onDuplicate?: (id: string) => void;
  onToggleFavorite?: (id: string, type: WorkspaceItemType) => void;
  onMove?: (id: string, type: WorkspaceItemType) => void;
  onDelete?: (id: string, type: WorkspaceItemType, name?: string) => void;
  onRestore?: (id: string, type: WorkspaceItemType) => void;
  onDeletePermanently?: (id: string, type: WorkspaceItemType) => void;
  onPreview?: (id: string, type: WorkspaceItemType) => void;
  onResumeDraft?: (id: string) => void;
  /** Drag and drop handlers */
  onDragStart?: (e: React.DragEvent, id: string, type: WorkspaceItemType) => void;
  onDragEnd?: () => void;
  onDragOver?: (e: React.DragEvent, id: string) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, id: string) => void;
  isDraggingItem?: (id: string) => boolean;
  isDraggingOverFolder?: (id: string) => boolean;
}

export function FolderGridView({
  items,
  viewMode,
  isTrash,
  canEdit = true,
  onKeyDown,
  onDuplicate,
  onToggleFavorite,
  onMove,
  onDelete,
  onRestore,
  onDeletePermanently,
  onPreview,
  onResumeDraft,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  isDraggingItem,
  isDraggingOverFolder,
}: FolderGridViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Selection store - single source of truth
  const { isSelected, lastSelectedId } = useSelectionStore();

  // Re-open preview when going from multi-select back to single selection
  useSelectionPreviewSync({ items, onPreview });

  // Compute all item IDs for range selection
  const allItemIds = useMemo(() => items.map((item) => item.id), [items]);

  // Scroll selected item into view when selection changes via keyboard
  useEffect(() => {
    if (lastSelectedId) {
      const element = itemRefs.current.get(lastSelectedId);
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [lastSelectedId]);

  return (
    <div
      ref={containerRef}
      className="flex flex-wrap gap-1"
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      {items.map((item) => (
        <div
          key={`${item.type}-${item.id}`}
          ref={(el) => {
            if (el) {
              itemRefs.current.set(item.id, el);
            } else {
              itemRefs.current.delete(item.id);
            }
          }}
        >
          <WorkspaceItemCard
            id={item.id}
            type={item.type}
            name={item.name}
            description={item.description}
            isFavorite={item.isFavorite}
            itemCount={item.itemCount}
            versionCount={item.versionCount}
            updatedAt={item.updatedAt}
            createdAt={item.createdAt}
            viewMode={viewMode}
            status={item.status}
            connectorType={item.connectorType}
            isTrash={isTrash}
            canEdit={canEdit}
            isSelected={isSelected(item.id)}
            allItemIds={allItemIds}
            onDuplicate={onDuplicate}
            onToggleFavorite={onToggleFavorite}
            onMove={onMove}
            onDelete={onDelete}
            onRestore={onRestore}
            onDeletePermanently={onDeletePermanently}
            onPreview={onPreview}
            onResumeDraft={onResumeDraft}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            isDragging={isDraggingItem?.(item.id)}
            isDropTarget={item.type === 'folder' && isDraggingOverFolder?.(item.id)}
          />
        </div>
      ))}
    </div>
  );
}
